import React from "react"

import { Modal, Button, DisplayError, Select, Form, TextArea, Input, Field, SelectOption } from "@fider/components"
import { Post } from "@fider/models"

import { actions, notify, Failure, Fider } from "@fider/services"
import { PostSearch } from "./PostSearch"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"

interface ResponseModalProps {
  post: Post
  showModal: boolean
  onCloseModal: () => void
  onResponded?: () => void
}

interface ResponseModalState {
  status: string
  text: string
  originalNumber: number
  targetDate: string
  owner: string
  externalLink: string
  error?: Failure
}

export class ResponseModal extends React.Component<ResponseModalProps, ResponseModalState> {
  constructor(props: ResponseModalProps) {
    super(props)

    this.state = {
      status: this.props.post.status,
      originalNumber: 0,
      text: this.props.post.response ? this.props.post.response.text : "",
      targetDate: this.props.post.targetDate,
      owner: this.props.post.owner,
      externalLink: this.props.post.externalLink,
    }
  }

  private submit = async () => {
    const result = await actions.respond(this.props.post.number, this.state)
    if (!result.ok) {
      this.setState({
        error: result.error,
      })
      return
    }

    if (this.state.targetDate !== this.props.post.targetDate) {
      const targetDateResult = await actions.setPostTargetDate(this.props.post.number, this.state.targetDate)
      if (!targetDateResult.ok) {
        this.setState({ error: targetDateResult.error })
        notify.error(<Trans id="showpost.responseform.targetdate.saveerror">Failed to save the target date</Trans>)
        return
      }
    }

    if (this.state.owner !== this.props.post.owner) {
      const ownerResult = await actions.setPostOwner(this.props.post.number, this.state.owner)
      if (!ownerResult.ok) {
        this.setState({ error: ownerResult.error })
        notify.error(<Trans id="showpost.responseform.owner.saveerror">Failed to save the owner</Trans>)
        return
      }
    }

    if (this.state.externalLink !== this.props.post.externalLink) {
      const externalLinkResult = await actions.setPostExternalLink(this.props.post.number, this.state.externalLink)
      if (!externalLinkResult.ok) {
        this.setState({ error: externalLinkResult.error })
        notify.error(<Trans id="showpost.responseform.externallink.saveerror">Failed to save the external link</Trans>)
        return
      }
    }

    if (this.props.onResponded) {
      this.props.onResponded()
    } else {
      location.reload()
    }
  }

  private setStatus = (opt?: SelectOption) => {
    if (opt) {
      this.setState({ status: opt.value })
    }
  }

  private setOriginalNumber = (originalNumber: number) => {
    this.setState({ originalNumber })
  }

  private setText = (text: string) => {
    this.setState({ text })
  }

  private setTargetDate = (opt?: SelectOption) => {
    this.setState({ targetDate: opt ? opt.value : "" })
  }

  private setOwner = (opt?: SelectOption) => {
    this.setState({ owner: opt ? opt.value : "" })
  }

  private setExternalLink = (externalLink: string) => {
    this.setState({ externalLink })
  }

  public render() {
    // "Duplicate" is a reserved status handled specially by the backend (see
    // enum.PostStatusDuplicate) — it's a valid target of this action but is
    // never a row in fider.postStatuses, so it's appended manually.
    const options = [
      ...Fider.session.postStatuses.map((s) => ({ value: s.slug, label: s.name })),
      { value: "duplicate", label: i18n._({ id: "enum.poststatus.duplicate", message: "Duplicate" }) },
    ]

    const modal = (
      <Modal.Window isOpen={this.props.showModal} onClose={this.props.onCloseModal} center={false} size="large">
        <Modal.Content>
          <Form error={this.state.error} className="c-response-form">
            <Select field="status" label="Status" defaultValue={this.state.status} options={options} onChange={this.setStatus} />
            <Select
              field="targetDate"
              label={i18n._({ id: "showpost.responseform.targetdate.label", message: "Estimated timeframe" })}
              defaultValue={this.state.targetDate}
              options={[
                { value: "", label: i18n._({ id: "showpost.responseform.targetdate.unset", message: "—" }) },
                ...Fider.session.postTargetDates.map((t) => ({ value: t.slug, label: t.name })),
              ]}
              onChange={this.setTargetDate}
            />
            <Select
              field="owner"
              label={i18n._({ id: "showpost.responseform.owner.label", message: "Owner" })}
              defaultValue={this.state.owner}
              options={[
                { value: "", label: i18n._({ id: "showpost.responseform.owner.unset", message: "—" }) },
                ...Fider.session.postOwners.map((o) => ({ value: o.slug, label: o.name })),
              ]}
              onChange={this.setOwner}
            />
            <Input
              field="externalLink"
              label={i18n._({ id: "showpost.responseform.externallink.label", message: "External link" })}
              value={this.state.externalLink}
              maxLength={500}
              onChange={this.setExternalLink}
              placeholder={i18n._({
                id: "showpost.responseform.externallink.placeholder",
                message: "https://...",
              })}
            />
            {this.state.status === "duplicate" ? (
              <>
                <Field>
                  <PostSearch exclude={[this.props.post.number]} onChanged={this.setOriginalNumber} />
                </Field>
                <DisplayError fields={["originalNumber"]} error={this.state.error} />
                <span className="text-muted">
                  <Trans id="showpost.responseform.message.mergedvotes">Votes from this post will be merged into original post.</Trans>
                </span>
              </>
            ) : (
              <TextArea
                field="text"
                onChange={this.setText}
                value={this.state.text}
                minRows={5}
                placeholder={i18n._({
                  id: "showpost.responseform.text.placeholder",
                  message: "What's going on with this post? Let your users know what are your plans...",
                })}
              />
            )}
          </Form>
        </Modal.Content>

        <Modal.Footer>
          <Button variant="primary" onClick={this.submit}>
            <Trans id="action.submit">Submit</Trans>
          </Button>
          <Button variant="tertiary" onClick={this.props.onCloseModal}>
            <Trans id="action.cancel">Cancel</Trans>
          </Button>
        </Modal.Footer>
      </Modal.Window>
    )

    return modal
  }
}
